import { FC, useMemo } from 'react'
import { SocialIcon } from 'react-social-icons'
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  WhatsappShareButton,
  WhatsappIcon,
  PinterestShareButton,
  PinterestIcon,
} from 'react-share'

import { StyledButtonsContainer } from './styled'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'

const installText = () => 'Here is where i buy products'

// QRM-594: only absolute http(s) URLs may be rendered as profile links —
// anything else (empty string, javascript:, relative path) counts as unconfigured
const isValidProfileLink = (link?: string): link is string =>
  Boolean(link && /^https?:\/\//i.test(link.trim()))

export const ShareButtons = () => {
  const interfaceStyles = useMySelector((state) => state.app.interfaceStyles)
  const companyData = useMySelector((state) => state.app.companyData)
  const { guid, registerId, dataEnc } = handleLocalStorage([
    'guid',
    'registerId',
    'dataEnc',
  ])

  const URL = useMemo(() => {
    return dataEnc
      ? `${process.env.REACT_APP_URL}?data=${dataEnc}`
      : guid
      ? `${process.env.REACT_APP_URL}?guid=${guid}${
          registerId ? '&register=' + registerId : ''
        }`
      : ''
  }, [dataEnc, guid, registerId])

  const profileLinks = useMemo(() => {
    const configured: Array<{ network: string; href: string }> = [
      { network: 'facebook', href: companyData?.FacebookLink },
      { network: 'x', href: companyData?.TwitterLink },
      { network: 'whatsapp', href: companyData?.WhatsAppLink },
      { network: 'pinterest', href: companyData?.PinterestLink },
    ]
      .filter((item): item is { network: string; href: string } =>
        isValidProfileLink(item.href)
      )
      .map(({ network, href }) => ({ network, href: href.trim() }))
    return configured
  }, [companyData])

  // QRM-594: when the restaurant configured its own profile links (console,
  // hssoft.com-Companies), the icons link there; otherwise fall back to the
  // legacy share buttons
  if (profileLinks.length) {
    return (
      <StyledButtonsContainer>
        {profileLinks.map(({ network, href }) => (
          <SocialIcon
            key={network}
            network={network}
            url={href}
            target="_blank"
            rel="noopener noreferrer"
          />
        ))}
      </StyledButtonsContainer>
    )
  }

  return (
    <StyledButtonsContainer>
      <FacebookShareButton
        key={`${URL}_fb`}
        quote={installText()}
        title={installText()}
        url={URL}
      >
        <Icon I={FacebookIcon} />
      </FacebookShareButton>
      <TwitterShareButton
        key={`${URL}_twitter`}
        title={installText()}
        url={URL}
      >
        <SocialIcon network="x" />
      </TwitterShareButton>
      <WhatsappShareButton
        key={`${URL}_whatsapp`}
        title={installText()}
        url={URL}
      >
        <Icon I={WhatsappIcon} />
      </WhatsappShareButton>
      <PinterestShareButton
        key={`${URL}_pinterest`}
        media={interfaceStyles.appIcon || interfaceStyles.logoImage}
        description={installText()}
        url={URL}
      >
        <Icon I={PinterestIcon} />
      </PinterestShareButton>
    </StyledButtonsContainer>
  )
}

const Icon: FC<any> = ({ I }) => <I round size={50} />
